/*
 * Vesper header
 * - <sticky-header>: sticky modes, --header-height, scrolled state, hide on scroll down / reveal on scroll up
 * - Desktop menus ([data-hover-menu] header-menu elements): open on hover with intent delay, one at a time
 * - <products-mega-menu>: switches the product panel when a collection tab is hovered or focused
 * - <vesper-drawer>: slide-in navigation drawer with focus trap
 */
(() => {
  const desktopHover = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 990px)');

  /* ---------- Sticky header ---------- */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.shopify-section') || document.querySelector('.section-header');
      if (!this.section) return;
      this.type = this.dataset.stickyType || 'none';
      this.lastY = window.scrollY;
      this.preventReveal = false;
      this.section.classList.toggle('logo-shrink', this.type === 'reduce-logo-size');

      this.setHeight = () => {
        document.documentElement.style.setProperty('--header-height', `${this.section.offsetHeight}px`);
      };
      this.setHeight();
      this.resizeObserver = new ResizeObserver(() => this.setHeight());
      this.resizeObserver.observe(this.section);

      this.onScroll = () => {
        if (this.ticking) return;
        this.ticking = true;
        requestAnimationFrame(() => {
          this.ticking = false;
          this.update();
        });
      };
      this.onPreventReveal = () => {
        this.preventReveal = true;
        clearTimeout(this.preventTimer);
        this.preventTimer = setTimeout(() => (this.preventReveal = false), 100);
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.addEventListener('preventHeaderReveal', this.onPreventReveal);
      this.update();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      this.removeEventListener('preventHeaderReveal', this.onPreventReveal);
      this.resizeObserver?.disconnect();
    }

    // Document position where the header starts (sections above it in the header group are not sticky).
    startOffset() {
      const prev = this.section.previousElementSibling;
      return prev ? prev.getBoundingClientRect().bottom + window.scrollY : 0;
    }

    get isBusy() {
      return (
        this.section.classList.contains('menu-open') ||
        !!this.querySelector('details[open]') ||
        !!this.querySelector('predictive-search[open]')
      );
    }

    update() {
      const y = window.scrollY;
      const start = this.startOffset();
      const height = this.section.offsetHeight;
      const past = y > start + height * 0.5;
      this.section.classList.toggle('scrolled-past-header', past);

      if (this.type === 'on-scroll-up') {
        const goingDown = y > this.lastY;
        if (y <= start) {
          this.reset();
        } else if (goingDown && y > start + height && !this.isBusy) {
          this.hide();
        } else if (!goingDown && !this.preventReveal) {
          this.reveal();
        } else if (this.preventReveal) {
          this.hide();
        }
      }
      this.lastY = y;
      this.setStickyOffset();
    }

    // --header-sticky-offset: space the pinned header currently covers at the top of the viewport
    // (used by sticky content such as the product info column).
    setStickyOffset() {
      const pinned = this.type !== 'none' && !this.section.classList.contains('shopify-section-header-hidden');
      const value = pinned ? `${this.section.offsetHeight}px` : '0px';
      if (value !== this.stickyOffset) {
        this.stickyOffset = value;
        document.documentElement.style.setProperty('--header-sticky-offset', value);
      }
    }

    hide() {
      if (this.type !== 'on-scroll-up') return;
      this.section.classList.add('shopify-section-header-hidden', 'animate');
      this.closeMenus();
    }

    reveal() {
      if (this.type !== 'on-scroll-up') return;
      this.section.classList.add('animate');
      this.section.classList.remove('shopify-section-header-hidden');
      this.setStickyOffset();
    }

    reset() {
      this.section.classList.remove('shopify-section-header-hidden', 'animate');
    }

    closeMenus() {
      this.querySelectorAll('header-menu').forEach((menu) => menu.close?.());
      const search = this.querySelector('details-modal');
      if (search?.isOpen?.()) search.close(false);
    }
  }
  if (!customElements.get('sticky-header')) customElements.define('sticky-header', StickyHeader);

  /* ---------- Desktop menus: hover intent + single open menu ---------- */
  const menus = () => [...document.querySelectorAll('.vheader [data-hover-menu]')];

  function setOpen(menu, open) {
    const details = menu.querySelector(':scope > details');
    const summary = details?.querySelector(':scope > summary');
    if (!details) return;
    if (open) {
      details.setAttribute('open', '');
      summary.setAttribute('aria-expanded', 'true');
    } else {
      details.removeAttribute('open');
      summary.setAttribute('aria-expanded', 'false');
      details.querySelectorAll('details[open]').forEach((d) => d.removeAttribute('open'));
    }
  }

  function closeOthers(except) {
    menus().forEach((m) => m !== except && setOpen(m, false));
  }

  function bindMenus(root = document) {
    root.querySelectorAll('.vheader [data-hover-menu]:not([data-bound])').forEach((menu) => {
      menu.setAttribute('data-bound', '');
      const details = menu.querySelector(':scope > details');
      const summary = details.querySelector(':scope > summary');
      summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
      let timer;
      let openedByHover = false;

      menu.addEventListener('mouseenter', () => {
        if (!desktopHover.matches) return;
        clearTimeout(timer);
        timer = setTimeout(() => {
          closeOthers(menu);
          if (!details.open) {
            openedByHover = true;
            setOpen(menu, true);
          }
        }, 70);
      });
      menu.addEventListener('mouseleave', () => {
        if (!desktopHover.matches) return;
        clearTimeout(timer);
        timer = setTimeout(() => {
          openedByHover = false;
          setOpen(menu, false);
        }, 180);
      });
      summary.addEventListener('click', (event) => {
        if (openedByHover && details.open) {
          // Already opened by hovering: a click should not immediately close it.
          event.preventDefault();
          openedByHover = false;
          return;
        }
        closeOthers(menu);
        requestAnimationFrame(() => summary.setAttribute('aria-expanded', details.open ? 'true' : 'false'));
      });
      details.addEventListener('keyup', (event) => {
        if (event.key !== 'Escape') return;
        setOpen(menu, false);
        summary.focus();
      });
    });
  }

  document.addEventListener('click', (event) => {
    if (event.target.closest('.vheader [data-hover-menu]')) return;
    menus().forEach((m) => setOpen(m, false));
  });

  /* ---------- Products mega menu tabs ---------- */
  class ProductsMegaMenu extends HTMLElement {
    connectedCallback() {
      this.tabs = [...this.querySelectorAll('[data-tab]')];
      this.panels = [...this.querySelectorAll('[data-panel]')];
      this.tabs.forEach((tab) => {
        const activate = () => this.activate(tab.dataset.tab);
        tab.addEventListener('mouseenter', activate);
        tab.addEventListener('focus', activate);
      });
    }
    activate(id) {
      this.tabs.forEach((t) => t.classList.toggle('is-active', t.dataset.tab === id));
      this.panels.forEach((p) => {
        const on = p.dataset.panel === id;
        p.hidden = !on;
        if (on) p.classList.add('is-entering');
        if (on) requestAnimationFrame(() => requestAnimationFrame(() => p.classList.remove('is-entering')));
      });
    }
  }
  if (!customElements.get('products-mega-menu')) customElements.define('products-mega-menu', ProductsMegaMenu);

  /* ---------- Navigation drawer ---------- */
  class VesperDrawer extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector(':scope > details');
      this.summary = this.details.querySelector(':scope > summary');
      this.panel = this.details.querySelector('.vdrawer__panel');
      this.summary.setAttribute('aria-expanded', 'false');
      this.summary.setAttribute('aria-controls', this.panel.id);
      this.summary.addEventListener('click', (event) => {
        event.preventDefault();
        this.details.open ? this.close() : this.open();
      });
      this.querySelectorAll('[data-drawer-close]').forEach((el) => el.addEventListener('click', () => this.close()));
      this.addEventListener('keyup', (event) => {
        if (event.key === 'Escape') this.close();
      });
      // Smooth accordions for nested menus.
      this.querySelectorAll('.vdrawer__group > summary').forEach((s) =>
        s.addEventListener('click', (event) => this.toggleGroup(event, s.parentElement))
      );
      this.mq = window.matchMedia('(min-width: 990px)');
      this.onBreakpoint = () => {
        if (this.details.open && this.mq.matches && !this.closest('.vheader--menu-drawer')) this.close(false);
      };
      this.mq.addEventListener('change', this.onBreakpoint);
    }

    disconnectedCallback() {
      this.mq?.removeEventListener('change', this.onBreakpoint);
      document.body.classList.remove('vdrawer-lock');
    }

    open() {
      this.section = this.closest('.shopify-section');
      this.details.setAttribute('open', '');
      this.summary.setAttribute('aria-expanded', 'true');
      this.section?.classList.add('menu-open');
      document.body.classList.add('vdrawer-lock');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.details.classList.add('is-open'));
      });
      const done = () => {
        if (typeof trapFocus === 'function') trapFocus(this.panel, this.panel.querySelector('.vdrawer__link, a, button'));
      };
      setTimeout(done, 250);
    }

    close(focusToggle = true) {
      if (!this.details.open) return;
      this.details.classList.remove('is-open');
      this.summary.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('vdrawer-lock');
      const finish = () => {
        this.details.removeAttribute('open');
        this.section?.classList.remove('menu-open');
        this.querySelectorAll('.vdrawer__group--panel[open]').forEach((g) => g.removeAttribute('open'));
      };
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      setTimeout(finish, reduced ? 0 : 380);
      if (typeof removeTrapFocus === 'function') removeTrapFocus(focusToggle ? this.summary : null);
    }

    toggleGroup(event, group) {
      // Top-level submenus slide in as a panel (CSS); only nested accordions animate their height.
      if (group.classList.contains('vdrawer__group--panel')) return;
      const content = group.querySelector(':scope > ul');
      if (!content || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      event.preventDefault();
      if (group.dataset.animating) return;
      group.dataset.animating = 'true';
      const opening = !group.open;
      if (opening) group.setAttribute('open', '');
      const full = content.scrollHeight;
      const from = opening ? 0 : full;
      const to = opening ? full : 0;
      const anim = content.animate(
        [
          { height: `${from}px`, opacity: opening ? 0 : 1 },
          { height: `${to}px`, opacity: opening ? 1 : 0 },
        ],
        { duration: 280, easing: 'cubic-bezier(0.2, 0.7, 0.2, 1)' }
      );
      content.style.overflow = 'hidden';
      anim.onfinish = () => {
        content.style.overflow = '';
        if (!opening) group.removeAttribute('open');
        delete group.dataset.animating;
      };
    }
  }
  if (!customElements.get('vesper-drawer')) customElements.define('vesper-drawer', VesperDrawer);

  /* ---------- Boot ---------- */
  bindMenus();
  document.addEventListener('shopify:section:load', (event) => bindMenus(event.target));
})();
